/**
 * Named lists, shared between the "add to list" control wherever it appears
 * (the registry table, the company profile pane) and the Lists page.
 *
 * `GET /lists` returns every list with its member company numbers, not just
 * counts, so this store can answer "which lists is company X already in?"
 * anywhere in the app from data it already has - no per-company round trip
 * for a popover to draw its checkmarks. Same reasoning as ShortlistProvider
 * and FavoritesProvider: one shared store, refreshed from the server response
 * after every mutation rather than guessed locally.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { listsApi } from './api.js';
import { useAuth } from './auth-context.jsx';

const ListsContext = createContext(null);

export function ListsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // "list:<id>" or "list:<id>:<companyNumber>" entries with a request in
  // flight, so one button can disable itself without freezing the rest.
  const [pending, setPending] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setLists([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await listsApi.list();
      setLists(data.lists ?? []);
      setError(null);
    } catch (caught) {
      setError(caught.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markPending = useCallback((key, active) => {
    setPending((current) => {
      const next = new Set(current);
      if (active) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const createList = useCallback(
    async (name, companyNumbers) => {
      markPending('create', true);
      try {
        const created = await listsApi.create(name, companyNumbers);
        await refresh();
        setError(null);
        return created;
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending('create', false);
      }
    },
    [markPending, refresh],
  );

  const renameList = useCallback(
    async (id, name) => {
      const key = `list:${id}`;
      markPending(key, true);
      try {
        await listsApi.rename(id, name);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refresh],
  );

  const deleteList = useCallback(
    async (id) => {
      const key = `list:${id}`;
      markPending(key, true);
      try {
        await listsApi.remove(id);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refresh],
  );

  const addToList = useCallback(
    async (id, companyNumber) => {
      const key = `list:${id}:${companyNumber}`;
      markPending(key, true);
      try {
        await listsApi.addItems(id, companyNumber);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refresh],
  );

  const removeFromList = useCallback(
    async (id, companyNumber) => {
      const key = `list:${id}:${companyNumber}`;
      markPending(key, true);
      try {
        await listsApi.removeItem(id, companyNumber);
        await refresh();
        setError(null);
      } catch (caught) {
        setError(caught.message);
        throw caught;
      } finally {
        markPending(key, false);
      }
    },
    [markPending, refresh],
  );

  // list id -> Set of member company numbers, so "is company X on list Y"
  // is a lookup rather than an array scan wherever a popover renders.
  const membership = useMemo(() => {
    const map = new Map();
    for (const list of lists) map.set(list.id, new Set(list.companyNumbers ?? []));
    return map;
  }, [lists]);

  const listsForCompany = useCallback(
    (companyNumber) => lists.filter((list) => membership.get(list.id)?.has(companyNumber)),
    [lists, membership],
  );

  const value = useMemo(
    () => ({
      lists,
      isLoading,
      error,
      isPending: (key) => pending.has(key),
      createList,
      renameList,
      deleteList,
      addToList,
      removeFromList,
      listsForCompany,
      refresh,
    }),
    [
      lists,
      isLoading,
      error,
      pending,
      createList,
      renameList,
      deleteList,
      addToList,
      removeFromList,
      listsForCompany,
      refresh,
    ],
  );

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

export function useLists() {
  const context = useContext(ListsContext);
  if (!context) throw new Error('useLists must be used inside a ListsProvider.');
  return context;
}
